import { Money } from "@hubrise/react-components"
import _ from "lodash"

import { OptionId, OptionListId } from "shared/hubrise/ids"
import * as Option from "shared/hubrise/utils/Option"
import { findWithOptionListById } from "shared/hubrise/utils/Option"
import * as OptionList from "shared/hubrise/utils/OptionList"
import { GenerateRefsOptions } from "shared/hubrise/utils/ref"

import { IOption, IOptionList } from "../models"

import type { Change, OperationContext } from "./types"
import { clearAllPrices, bulkUpdatePrices } from "./utils"

export type OptionOperations = {
  add(optionList: IOptionList, option: IOption): void
  delete(optionListId: OptionListId, optionId: OptionId): void
  update<T extends keyof IOption>(
    optionListId: OptionListId,
    optionId: OptionId,
    fieldName: T,
    value: IOption[T],
    options?: { collapse?: boolean },
  ): void
  removeAllRefs(): void
  generateRefs(options: GenerateRefsOptions): void
  enable(optionListId: OptionListId, optionId: OptionId, enabled: boolean, variantRef?: string): void
  enableAll(enabled: boolean, variantRef?: string): void
  updateVariantPrice(optionListId: OptionListId, optionId: OptionId, variantRef: string, price: Money | null): void
  bulkUpdatePrices(
    sourceVariantRef: string | null,
    targetVariantRef: string | null,
    skipAlreadySetPrices: boolean,
    optionListNames: Array<string> | null,
    calculateTargetPrice: (sourcePrice: Money) => Money,
  ): void
  clearAllPrices(variantRef: string): void
}

export default ({ catalog, pushState }: OperationContext): OptionOperations => ({
  add(optionList, option) {
    const newCatalog = OptionList.update(catalog, optionList.id, "options", [...optionList.options, option])
    pushState(newCatalog, { model: "option", operation: "add" })
  },

  delete(optionListId, optionId) {
    const newCatalog = Option.doDelete(catalog, optionListId, optionId)
    pushState(newCatalog, { model: "option", operation: "delete", id: optionId })
  },

  update(optionListId, optionId, fieldName, value, { collapse = true } = {}) {
    const newCatalog = Option.update(catalog, optionListId, optionId, fieldName, value)
    const change: Change = {
      model: "option",
      id: optionId,
      operation: "update",
      fieldName,
    }
    pushState(newCatalog, change, (previousChange) => collapse && _.isEqual(previousChange, change))
  },

  removeAllRefs() {
    const newCatalog = Option.removeAllRefs(catalog)
    pushState(newCatalog, { model: "option", operation: "removeAllRefs" })
  },

  generateRefs(options) {
    const newCatalog = Option.generateRefs(catalog, options)
    pushState(newCatalog, { model: "option", operation: "generateRefs" })
  },

  enable(optionListId, optionId, enabled, variantRef) {
    const newCatalog = Option.enable(catalog, optionListId, optionId, enabled, variantRef)
    const change: Change = {
      model: "option",
      id: optionId,
      operation: "enable",
      variantRef,
    }
    pushState(newCatalog, change, (previousChange) => _.isEqual(previousChange, change))
  },

  enableAll(enabled, variantRef) {
    const newCatalog = Option.enableAll(catalog, enabled, variantRef)
    const change: Change = { model: "option", operation: "enableAll", variantRef }
    pushState(newCatalog, change)
  },

  updateVariantPrice(optionListId, optionId, variantRef, price) {
    const newCatalog = Option.setVariantPrice(catalog, optionListId, optionId, variantRef, price)
    const change: Change = {
      model: "option",
      id: optionId,
      operation: "updateVariantPrice",
      variantRef,
    }
    pushState(newCatalog, change, (previousChange) => _.isEqual(previousChange, change))
  },

  bulkUpdatePrices(sourceVariantRef, targetVariantRef, skipAlreadySetPrices, optionListNames, calculateTargetPrice) {
    const newCatalog = bulkUpdatePrices<IOption>(
      "optionLists",
      catalog,
      sourceVariantRef,
      targetVariantRef,
      skipAlreadySetPrices,
      calculateTargetPrice,
      optionListNames
        ? (option) => optionListNames.includes(findWithOptionListById(catalog, option.id).optionList.name)
        : undefined,
    )
    const change: Change = { model: "option", operation: "bulkUpdatePrices" }
    pushState(newCatalog, change)
  },

  clearAllPrices(variantRef: string) {
    const newCatalog = clearAllPrices("optionLists", catalog, variantRef)
    const change: Change = { model: "option", operation: "bulkUpdatePrices" }
    pushState(newCatalog, change)
  },
})
